import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MapPin, Globe, ArrowRight } from 'lucide-react';
import whereWeOperatePhoto from '../../assets/forWhereWeOperate.jpg';

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay } }
});

const WhereWeOperate = () => {
  const { t } = useTranslation();

  const coverageItems = [
    { icon: <MapPin size={15} color="#4792f4" />, text: t('coverageItem1') },
    { icon: <ArrowRight size={15} color="#4792f4" />, text: t('coverageItem2') },
    { icon: <Globe size={15} color="#4792f4" />, text: t('coverageItem3') },
  ];

  return (
    <section id="where-we-operate">
      <style>{`
        .wwo-section {
          background: linear-gradient(135deg, #0f2350 0%, #1b3f82 50%, #2563eb 100%);
          border-top: 1px solid rgba(71,146,244,0.30);
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3.5rem;
        }

        /* ── Header ── */
        .wwo-header { text-align: center; }
        .wwo-title {
          font-size: clamp(1.8rem, 3vw, 2.6rem);
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 0.75rem;
          letter-spacing: -0.02em;
        }
        .wwo-intro {
          max-width: 780px;
          margin: 0 auto;
          font-size: 1rem;
          color: #c0d8ff;
          line-height: 1.8;
        }

        /* ── Coverage row ── */
        .wwo-coverage-row {
          max-width: 1100px;
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          align-items: stretch;
        }

        /* Left — list card */
        .wwo-coverage-card {
          background: #f8fbff;
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 16px;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        .wwo-coverage-label {
          font-size: 0.78rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #4792f4;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          margin: 0;
        }
        .wwo-coverage-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }
        .wwo-coverage-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          font-size: 0.95rem;
          color: #1a3260;
          line-height: 1.55;
          background: rgba(255,255,255,0.88);
          border: 1px solid rgba(71,146,244,0.12);
          border-radius: 10px;
          padding: 0.7rem 1rem;
        }
        .wwo-coverage-list li svg { flex-shrink: 0; margin-top: 2px; }

        /* Right — map visual */
        .wwo-map-card {
          background: linear-gradient(135deg, #4792f4 0%, #1a5bbf 100%);
          border-radius: 16px;
          overflow: hidden;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          min-height: 260px;
          box-shadow: 0 12px 32px rgba(71,146,244,0.28);
        }
        .wwo-map-dots {
          position: absolute;
          inset: 0;
          background-image:
            radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px);
          background-size: 28px 28px;
        }
        .wwo-map-globe {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: rgba(255,255,255,0.08);
          border: 2px solid rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .wwo-map-globe::before {
          content: '';
          position: absolute;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          border: 1.5px solid rgba(255,255,255,0.15);
        }
        .wwo-map-footer {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 2;
          background: rgba(0,0,0,0.45);
          backdrop-filter: blur(6px);
          padding: 1.1rem 1.5rem;
          border-radius: 0 0 16px 16px;
        }
        .wwo-map-footer p {
          margin: 0;
          font-size: 0.88rem;
          color: rgba(255,255,255,0.9);
          line-height: 1.5;
        }
        .wwo-map-footer strong {
          color: #ffffff;
          display: block;
          font-size: 0.95rem;
          margin-bottom: 0.2rem;
        }

        @media (max-width: 760px) {
          .wwo-coverage-row { grid-template-columns: 1fr; }
          .wwo-map-card { min-height: 200px; }
        }
      `}</style>

      <div className="wwo-section">

        {/* Title + Intro */}
        <motion.div
          className="wwo-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp(0)}
        >
          <h2 className="wwo-title">{t('whereWeOperateTitle')}</h2>
          <p className="wwo-intro">{t('whereWeOperateIntro')}</p>
        </motion.div>

        {/* Coverage row */}
        <motion.div
          className="wwo-coverage-row"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
        >
          {/* Coverage list */}
          <motion.div className="wwo-coverage-card" variants={fadeUp(0)}>
            <p className="wwo-coverage-label">
              <MapPin size={13} />
              {t('coverageLabel')}
            </p>
            <ul className="wwo-coverage-list">
              {coverageItems.map((item, i) => (
                <li key={i}>
                  {item.icon}
                  {item.text}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Map visual */}
          <motion.div className="wwo-map-card" variants={fadeUp(0.1)}>
            <img
              src={whereWeOperatePhoto}
              alt="Where we operate"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: '260px', borderRadius: '16px' }}
            />
            <div className="wwo-map-footer">
              <p>
                <strong>{t('whereWeOperateGeorgia')}</strong>
                {t('whereWeOperateBorders')}
              </p>
            </div>
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
};

export default WhereWeOperate;
